import './select.css';

import _ from 'lodash';
import { Portal } from 'react-portal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import postal from 'postal';
import { usePopper } from 'react-popper';

import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { stringsStartsWithIgnoreCase } from '../util';
import useDeepCompareEffect from '../hooks/useDeepCompareEffect';
import { focusNextTabable } from '../util/inputUtils';

export interface SelectItem {
  value: string|number,
  label: string,
  activeLabel?: string|undefined,
  icon?: any,
  iconHeight?: string|undefined,
  color?: string,
}

function Select({
  selectId = undefined,
  value,
  onChange = () => {},
  disabled = false,
  items = [],
  selectWidth = undefined,
  hideCaretWhenDisabled = false,
  blankWhenDisabled = false,
  alignCenter = false,
  onEnterSameValue,
  onTabNext = undefined,
  onTabPrevious = undefined,
  placeholder = undefined,
  hideCaret = false,
  maxLength = undefined,
  rollBackPrevValue = true,
  applySearchWithEnter = undefined,
  name=undefined,
  notFocusNextEleWhenSelectItem = false,
  roundborder = false,
  focusable = true,
  forExSerach = false,
  useBlank = false,
  inactiveText = undefined,
  clearInactiveText = false,
  noPaddingLeft = false,
}: {
  selectId?: string|undefined,
  value: string|number,
  onChange: (e:React.ChangeEvent<HTMLInputElement>) => void,
  disabled?: boolean,
  items?: Array<SelectItem>,
  selectWidth?: string|undefined,
  hideCaretWhenDisabled?: boolean,
  blankWhenDisabled?: boolean,
  alignCenter?: boolean,
  onEnterSameValue?: VoidFunction,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  placeholder?: string|undefined,
  hideCaret?: boolean,
  maxLength?: number|undefined,
  rollBackPrevValue?: boolean,
  applySearchWithEnter?: (() => void)|undefined,
  name?:string|undefined,
  notFocusNextEleWhenSelectItem?: boolean, //oeh > po list > c.origin 데이터를 선택한 경우엔 Focus가 유지돼야함
  roundborder?: boolean, 
  focusable?: boolean,
  forExSerach?: boolean,
  useBlank? : boolean,
  inactiveText?: string|undefined,
  clearInactiveText?: boolean
  noPaddingLeft?: boolean
}) {
  // const blankWhenDisabled = false;
  const channel = postal.channel('ifms');

  const autocompleteOffGuard = _.uniqueId('autocomplete-off-');

  const itemsRef = useRef(items);

  const containerIdRef = useRef(_.uniqueId('ifms-select-container-'));
  const selectInputIdRef = useRef(selectId || _.uniqueId('ifms-select-'));
  let openSelectSubscription: ISubscriptionDefinition<any> | null = null;

  function subscribeChannels() {
    openSelectSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.select-open',
      callback: (data) => openWithoutFilter(data.targetId)
    });
  }

  function unsubscribeChannels() {
    if (openSelectSubscription) {
      postal.unsubscribe(openSelectSubscription);
    }
  }

  const [, _setSelectedItem] = useState(
    (value !== undefined && value !== null)
      ? items.findIndex(x => x.value === value)
      : -1
  );
  const selectedItemRef = useRef(
    (value !== undefined && value !== null)
      ? items.findIndex(x => x.value === value)
      : -1
  );

  function setSelectedItem(index:number) {
    scrollIntoView(index);
    selectedItemRef.current = index;
    _setSelectedItem(index);
  }

  const [open, _setOpen] = useState(false);
  const openRef = useRef(false);

  const [filteredItems, _setFilteredItems] = useState<Array<SelectItem>>([...items]);
  const [stringValue, _setStringValue] = useState('');
  const stringValueRef = useRef('');
  const skipStringValueCheckRef = useRef(false);

  const lastSelectValueRef = useRef(value);
  const fieldClickRef = useRef(false);
  const listItemSelectRef = useRef(false);

  const filteredItemsRef = useRef<Array<SelectItem>>([...items]);

  function setFilteredItems(fi: Array<SelectItem>) {
    filteredItemsRef.current = fi;
    _setFilteredItems(fi);
  }

  const selectMenuContainerRef = useRef(null);

  const [referenceElement, _setReferenceElement] = useState<HTMLInputElement|null>(null);
  const [popperElement, _setPopperElement] = useState<HTMLDivElement|null>(null);
  const [, _setArrowElement] = useState<HTMLDivElement|null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed'
  });

  const popperRef = useRef<HTMLDivElement|null>(null);
  const referenceRef = useRef<HTMLInputElement|null>(null);
  const arrowElementRef = useRef<HTMLDivElement|null>(null);

  useEffect(() => {
    subscribeChannels();
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      unsubscribeChannels();
      document.removeEventListener('click', handleClickOutside, true);
    };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItemsRef.current]
  );

  useEffect(() => {
      if (open) {
        scrollIntoView(selectedItemRef.current);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  useEffect(() => {
      const item = items.find(x => x.value === value);
      if (item) {
        const sv = _.toString(item.label);
        if (sv !== stringValue && !open) {
          setStringValue(sv);
        }
      } else if (useBlank){
        setStringValue('');
      } else if (items.length > 1) {
        if (clearInactiveText) {
          notifyEmptyValue();
        } else if (inactiveText) {
          setStringValue(inactiveText);
        }
      } 
      // else if (items.length > 1 && specialAction > 0){
      //   if (specialAction == 1) {
      //     setStringValue('' + value);
      //   }
      //   else {
      //     notifyEmptyValue();
      //   }
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, items]
  );

  useDeepCompareEffect(() => {
      itemsRef.current = items;

      if (skipStringValueCheckRef.current) {
        skipStringValueCheckRef.current = false;
        return;
      }
      const fi = stringValue
        ? items.filter(x => {
          // if (typeof x.value === 'number') {
          //   return x.value === parseInt(stringValue);
          // }

          // return stringsStartsWithIgnoreCase(x.value as string, stringValue as string);
          return stringsStartsWithIgnoreCase(x.label, stringValue as string);
        })
        : [...items];

      setFilteredItems(fi);
      setSelectedItem(fi.length > 0 ? 0 : -1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stringValue, items]
  );

  function setStringValue(s:string) {
    stringValueRef.current = s;
    _setStringValue(s);

    const dataEntry = document.getElementById('ifms-filter-entry-container');
    if(dataEntry && !disabled)selectAllValue();
  }

  //Select all search value when the search pop-up is first opened
  function selectAllValue() {
    setTimeout(() => {
      if (referenceRef.current && items.find(x => x.label === referenceRef.current?.value) && !open && !listItemSelectRef.current && !fieldClickRef.current) {
        referenceRef.current?.select();
      }
    }, 30);
  }

  function isScrolledIntoView(container:HTMLElement, row:HTMLElement) {
    const trect = container.getBoundingClientRect();
    const rect = row.getBoundingClientRect();

    const viewStart = trect.top;
    const viewEnd = trect.top + trect.height;

    return rect.top >= viewStart && (rect.top + rect.height) <= viewEnd;
  }

  function scrollIntoView(index:number) {
    const container = document.getElementById(containerIdRef.current);
    const row = document.getElementById((name?`${name}-box-item-${index}`:`${containerIdRef.current}-item-${index}`));
    
    if (!row || !container) {
      return;
    }
    
    if (index < 0) {
      container.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      return;
    }

    if (isScrolledIntoView(container, row)) {
      return;
    }

    const newPos = row.offsetTop;

    container.scrollTo({
      top: newPos >= 0 ? newPos : 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  function setReferenceElement(e:HTMLInputElement|null) {
    referenceRef.current = e;
    _setReferenceElement(e);
  }

  function setPopperElement(e:HTMLDivElement|null) {
    popperRef.current = e;
    _setPopperElement(e);
  }

  function setArrowElement(e:HTMLDivElement|null) {
    arrowElementRef.current = e;
    _setArrowElement(e);
  }

  function openWithoutFilter(targetId?: string) {
    if (targetId && referenceRef.current?.id !== targetId) return;

    setOpen(!openRef.current);

    if (openRef.current) {
      // Show full list without filters
      let newSelectedItem = selectedItemRef.current;
      if (newSelectedItem >= 0) {
        const newFilteredItemValue = filteredItems[newSelectedItem].value;
        newSelectedItem = items.findIndex(x => x.value === newFilteredItemValue);
      }
      setFilteredItems([...items]);
      setSelectedItem(newSelectedItem);
    }

    if (referenceElement) {
      referenceElement.focus();
    }
  }

  function setOpen(v: boolean) {
    if (!referenceRef.current) return;

    openRef.current = disabled ? false : v;
    _setOpen(disabled ? false : v);
  }

  function processBlur() {
    if (selectedItemRef.current >= 0 && selectedItemRef.current < itemsRef.current.length) {
      if (referenceRef.current?.value) {
        const findItem = itemsRef.current.find(x => x.label === referenceRef.current?.value);
        if (findItem) {
          onListItemSelected(findItem);
          return;
        }
      }
      const lastSelectIndex = itemsRef.current.findIndex(x => x.value === lastSelectValueRef.current);
      setStringValue(_.toString(itemsRef.current[lastSelectIndex]?.label ?? ''));
      setOpen(false);
    }
    else {
      if (rollBackPrevValue) {
        const lastSelectIndex = itemsRef.current.findIndex(x => x.value === lastSelectValueRef.current);
        setStringValue(_.toString(itemsRef.current[lastSelectIndex]?.label ?? ''));
      }
      else {
        skipStringValueCheckRef.current = true;
        setStringValue('');
      }
      setOpen(false);
    }
  }

  function handleClickOutside(e:MouseEvent) {

    if (!popperRef || !referenceRef || !popperRef.current || !referenceRef.current || !arrowElementRef.current) {
      return;
    }

    const target = e.target as Element;

    if (!popperRef.current.contains(target) && !referenceRef.current.contains(target) && !arrowElementRef.current.contains(target)) {
      processBlur();
    }
  }

  // function blurInput() {
  //   if (referenceElement) {
  //     referenceElement.blur();
  //   }
  // }

  function notifyEmptyValue() {
    onChange({
      target: {
        value: '',
      }
    } as React.ChangeEvent<HTMLInputElement>);
    setStringValue('');
  }

  function isEditTableCell(element: Element): boolean {
    const divElements = document.querySelectorAll('div[id$="-editabletable"]');
    return Array.from(divElements).some(div => div.contains(element));
  }

  function onListItemSelected(item:SelectItem) {
    listItemSelectRef.current = true;
    const index = (item.value !== undefined && item.value !== null)
      ? filteredItemsRef.current.findIndex(x => x.value === item.value)
      : -1;
    
    setSelectedItem(index);

    if (item) {
      // if (item.value !== lastSelectValueRef.current) {
      if (item.value !== value) {
        onChange({
          target: {
            value: item.value,
          }
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        if (onEnterSameValue) {
          onEnterSameValue();
        }
        setStringValue(_.toString(item.label));
      }
    }

    // setTimeout(() => {
    //   if (referenceElement) {
    //     referenceElement.focus();
    //   }
    // }, 10);
    lastSelectValueRef.current = _.toString(item.value)

    //focus next tabable field after select
    setTimeout(() => {
      if (isEditTableCell(referenceRef.current as HTMLInputElement)) {
        channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: referenceRef.current });
    }
      else if (!notFocusNextEleWhenSelectItem && !applySearchWithEnter) {
        if (referenceRef.current)
          focusNextTabable(referenceRef.current)
      }
    }, 10)

    setOpen(false);
  }

  function clearSelection() {
    setSelectedItem(0);
  }

  // function handleInputClick(e:React.MouseEvent) {
  //   if (!openRef.current) {
  //     // Show full list without filters
  //     let newSelectedItem = selectedItemRef.current;
  //     if (newSelectedItem >= 0) {
  //       const newFilteredItemValue = filteredItems[newSelectedItem].value;
  //       newSelectedItem = items.findIndex(x => x.value === newFilteredItemValue);
  //     }
  //     setFilteredItems([...items]);
  //     setSelectedItem(newSelectedItem);

  //     setOpen(true);
  //     e.stopPropagation();
  //     e.preventDefault();
  //   }
  // }

  function processSpecialKeys(e:React.KeyboardEvent) {
    if (!e || !e.keyCode) {
      return;
    }

    switch(e.keyCode) {
      case 40: // Arrow down
        if (!openRef.current) {
          setFilteredItems([...items]);
          setOpen(true);
          e.preventDefault();
          e.stopPropagation();
        }
        else {
          if (selectedItemRef.current < filteredItems.length - 1) {
            skipStringValueCheckRef.current = true;
            setStringValue(filteredItems[selectedItemRef.current + 1].label);
            setSelectedItem(selectedItemRef.current + 1);
          }
        }
        break;
      case 38: // Arrow up
        if (selectedItemRef.current > 0) {
          skipStringValueCheckRef.current = true;
          setStringValue(filteredItems[selectedItemRef.current - 1].label);
          setSelectedItem(selectedItemRef.current - 1);
        }
        break;
      case 8: // Backspace
        clearSelection()
        break;
      case 9: // Tab
        if (openRef.current) {
          if (!!stringValue && selectedItemRef.current >= 0 && selectedItemRef.current < filteredItems.length) {
            onListItemSelected(filteredItems[selectedItemRef.current]);
            e.preventDefault();
            e.stopPropagation();
          } else {
            if (rollBackPrevValue) {
              const lastSelectIndex = items.findIndex(x => x.value === lastSelectValueRef.current);
              setStringValue(_.toString(itemsRef.current[lastSelectIndex]?.label ?? ''));
              setOpen(false);
            }
            else {
              notifyEmptyValue();
              setOpen(false);
            }
            if (isEditTableCell(referenceRef.current as HTMLInputElement)) {
              e.preventDefault();
              e.stopPropagation();
              setTimeout(() => {
                channel.publish('ifms.editable-table-onEnter', { backFocus: false, changeFocus: true, element: referenceRef.current });
              }, 10)
            }
          }
          //blurInput();
        }
        // if (onTabNext && !e.shiftKey) {
        //   const tabProcessed = onTabNext();
        //   if (tabProcessed) {
        //     e.preventDefault();
        //     e.stopPropagation();
        //     return;
        //   }
        // }
        if (e.shiftKey) {
          if (onTabPrevious) {
            const tabProcessed = onTabPrevious();
            if (tabProcessed) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        } else {
          if (onTabNext) {
            const tabProcessed = onTabNext();
            if (tabProcessed) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
          }
        }
        break;
      case 13: // Enter
        if (!openRef.current) {
          setFilteredItems([...items]);
          setOpen(true);
          const itemWithStringLabel = items.find(x => x.label === stringValueRef.current);
          const alreadySelected = itemWithStringLabel && itemWithStringLabel.value === value && (itemWithStringLabel.label !== '' || applySearchWithEnter);
          if (!alreadySelected) {
            
          }
          else {
            if (applySearchWithEnter)
            applySearchWithEnter();
          }
          e.stopPropagation();
          e.preventDefault();
        } else {
          if (selectedItemRef.current >= 0 && selectedItemRef.current < filteredItems.length) {
            onListItemSelected(filteredItems[selectedItemRef.current]);
          } else {
            notifyEmptyValue();
          }
          setOpen(false);

          e.stopPropagation();
          e.preventDefault();
        }
        break;
      default:
    }
  }

  const hasSelectedValue = items.some(x => x.value === value);
  const selectedBaseClass = hasSelectedValue ? 'ifms-select-with-value' : '';

  const baseClass = ((hideCaretWhenDisabled && disabled) || hideCaret) ? `ifms-select-no-caret ${selectedBaseClass}` : `ifms-select ${selectedBaseClass}`;

  let cssClass = "";
  if (blankWhenDisabled) cssClass = `${baseClass} e-ifms-input-blank e-outline ifms-tabable`;
  else cssClass = `${baseClass} e-ifms-input e-outline ifms-tabable`;

  if (focusable && !disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }

  const inputRect = referenceElement ? referenceElement.getBoundingClientRect() : null;

  let minWidth = `${inputRect ? inputRect.width : 100}px`;
  if (!!selectWidth) {
    minWidth = selectWidth;
  }

  const appliedPopperStyles = {
    ...styles.popper,
    minWidth,
  };

  const inputStyleBase: React.CSSProperties = roundborder ? { borderRadius: '5px' } : {};

  const inputArrowStyleBase: React.CSSProperties = roundborder ? { borderRadius: '0px 5px 5px 0px' } : {};

  const inputStyle: React.CSSProperties = alignCenter
    ? (
      forExSerach
      ? { ...inputStyleBase, textAlign: 'center', paddingRight :'17px'}
      : { ...inputStyleBase, textAlign: 'center', paddingLeft: ((hideCaretWhenDisabled && disabled) || hideCaret || noPaddingLeft) ? '0px' : '24px', paddingRight :  forExSerach ? '24px' : '0px'}
    )
    : { ...inputStyleBase };

  return (
    <div className="ifms-select-container">
      <input
        maxLength={maxLength}
        id={selectInputIdRef.current}
        name={name}
        type="text"
        ref={setReferenceElement}
        className={cssClass}
        style={inputStyle}
        value={stringValue}
        data-actual-value={value || ''}
        onChange={e => {
          setStringValue(e.target.value);
          if (!open) {
            setOpen(true);
          }
        }}
        disabled={disabled}
        onKeyDown={processSpecialKeys}
        //<230330. auto complete Off 적용
        //autoComplete={autocompleteOffGuard}
        autoComplete="off"
        list={autocompleteOffGuard}
        //list={selectInputIdRef.current}
        //230330.>
        data-tooltip="-from-value"
        
        data-tooltip-onlywhencutoff
        placeholder={placeholder}
        onClick={(e) => {
          if (hideCaret && !open) 
            setOpen(true);
          
          if (!disabled && !fieldClickRef.current) {
            (e.target as HTMLInputElement).select();
            fieldClickRef.current = true;
          }
        }}
        onBlur={() => {
          fieldClickRef.current = false;
        }}
      />
      {(!(hideCaretWhenDisabled && disabled) && !hideCaret) &&
        <div 
        style={inputArrowStyleBase}
          id={`${name}-select-drop-icon`}
          className={disabled ? 'ifms-select-drop-icon-container ifms-select-drop-icon-container-disabled' : 'ifms-select-drop-icon-container ifms-select-drop-icon-container-enabled'}
          onClick={() => {
            if (disabled) {
              return;
            }
            openWithoutFilter();
          }}
          ref={setArrowElement}
        >
          <FontAwesomeIcon icon={faAngleDown} className="ifms-select-drop-icon" />
        </div>
      }
      {open &&
        <Portal node={document && document.getElementById('ifms-app-poppers')}>
          <div id={containerIdRef.current} ref={setPopperElement} className="ifms-select-box" style={appliedPopperStyles} {...attributes.popper}>
            <div ref={selectMenuContainerRef}>
              {filteredItems.map((x, i) => (
                <div
                  id={(name?`${name}-box-item-${i}`:`${containerIdRef.current}-item-${i}`)}
                  key={x.value}
                  className={`${selectedItemRef.current === i ? 'ifms-select-item-selected ': 'ifms-select-item'}`}
                  onClick={(e:React.MouseEvent) =>{
                    onListItemSelected(x)
                    e.stopPropagation();
                  }}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  {x.color && (
                    <span 
                      className="ifms-select-color-indicator"
                      style={{ backgroundColor: x.color, display: 'inline-block', width: '13px', height: '13px', marginRight: '8px', verticalAlign: 'middle', border: '1px solid black' }}
                    />
                  )}
                  {x.icon && (
                    <img 
                      src={x.icon} 
                      alt='' 
                      style={{ height: x.iconHeight || '13px', marginRight: '5px' }} 
                    />
                  )}
                  {x.label}
                </div>
              ))}
            </div>
          </div>
        </Portal>
        }
    </div>
  );
}

Select.defaultProps = { 
  onChange: () => {},
  value: '',
  disabled: false,
  blankWhenDisabled: false,
  items: [],
  hideCaretWhenDisabled: false,
  alignCenter: false,
  hideCaret: false,
  rollBackPrevValue: true,
  forExSerach: false,
  useBlank: false,
  inactiveText:'',
  clearInactiveText: false,
};

Select.propTypes = {
  selectId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onEnterSameValue: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool.isRequired,
  blankWhenDisabled: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    activeLabel: PropTypes.string,
  })).isRequired,
  selectWidth: PropTypes.string,
  hideCaretWhenDisabled: PropTypes.bool.isRequired,
  hideCaret: PropTypes.bool.isRequired,
  alignCenter: PropTypes.bool.isRequired,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  placeholder: PropTypes.string,
  rollBackPrevValue: PropTypes.bool,
  roundborder : PropTypes.bool,
  forExSerach: PropTypes.bool,
  useBlank: PropTypes.bool,
  inactiveText: PropTypes.string,
  clearInactiveText: PropTypes.bool,
};


export default React.memo(Select);
